import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  DollarSign,
  Phone,
  Target,
  TrendingUp,
  Calendar,
  Wand2,
  RefreshCw,
} from "lucide-react";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { AIInsightCard } from "@/components/dashboard/AIInsightCard";
import { PipelineOverview } from "@/components/dashboard/PipelineOverview";
import { HotLeadsTable } from "@/components/dashboard/HotLeadsTable";
import { ActivityFeed } from "@/components/dashboard/ActivityFeed";
import { ContractPipelineWidget } from "@/components/dashboard/ContractPipelineWidget";
import { Button } from "@/components/ui/button";
import { useDashboardStats, useUserProfile, useAIInsights } from "@/hooks/useDashboard";
import { useRealtimeDashboard } from "@/hooks/useRealtimeDashboard";
import { Skeleton } from "@/components/ui/skeleton";
import { AISalesCoach } from "@/components/ai/AISalesCoach";
import { AIWeeklyReport } from "@/components/ai/AIWeeklyReport";
import { GuidedSalesWizard } from "@/components/wizard/GuidedSalesWizard";
import { AddLeadDialog } from "@/components/leads/AddLeadDialog";
import { AddDealDialog } from "@/components/deals/AddDealDialog";
import { LogCallDialog } from "@/components/calls/LogCallDialog";

export default function Dashboard() {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: profile } = useUserProfile();
  const { data: aiInsights, isLoading: insightsLoading, isFetching: insightsFetching, refetch: refetchInsights } = useAIInsights();
  const navigate = useNavigate();
  
  // Enable real-time updates
  useRealtimeDashboard();

  const firstName = profile?.first_name || "there";

  const handleStartAICall = () => {
    navigate("/app/calls");
  };

  const handleForecast = () => {
    navigate("/app/analytics");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-slide-up">
        <div>
          <h1 className="text-2xl font-bold">Good morning, {firstName}</h1>
          <p className="text-muted-foreground">
            Here's what's happening with your sales today.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AIWeeklyReport />
          <Button variant="outline" className="gap-2" onClick={() => setWizardOpen(true)}>
            <Wand2 className="h-4 w-4" />
            Sales Wizard
          </Button>
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />
            {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </Button>
          <Button className="gap-2 ai-glow" onClick={handleStartAICall}>
            <Phone className="h-4 w-4" />
            Start AI Call
          </Button>
        </div>
      </div>

      {/* Guided Sales Wizard */}
      <GuidedSalesWizard open={wizardOpen} onOpenChange={setWizardOpen} />

      {/* Quick Action Dialogs */}
      <AddLeadDialog open={addLeadOpen} onOpenChange={setAddLeadOpen} />
      <AddDealDialog open={addDealOpen} onOpenChange={setAddDealOpen} />
      <LogCallDialog open={logCallOpen} onOpenChange={setLogCallOpen} />
      
      {/* AI Insights */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">AI Insights</h2>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refetchInsights()}
            disabled={insightsFetching}
            className="gap-2 hover:bg-primary/10 hover:text-primary hover:border-primary/30"
          >
            <RefreshCw className={`h-4 w-4 ${insightsFetching ? 'animate-spin' : ''}`} />
            Refresh Insights
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 stagger-scale">
          {insightsLoading ? (
            <>
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </>
          ) : (
            aiInsights?.map((insight) => (
              <AIInsightCard
                key={insight.id}
                title={insight.title}
                description={insight.description}
                action={insight.action}
                onAction={() => insight.actionRoute && navigate(insight.actionRoute)}
              />
            ))
          )}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        {statsLoading ? (
          <>
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </>
        ) : (
          <>
            <MetricCard
              title="Active Leads"
              value={stats?.activeLeads?.toString() || "0"}
              change={12}
              changeLabel="vs last week"
              icon={Users}
              variant="primary"
            />
            <MetricCard
              title="Pipeline Value"
              value={`$${((stats?.pipelineValue || 0) / 1000).toFixed(0)}K`}
              change={8}
              changeLabel="vs last month"
              icon={DollarSign}
              variant="success"
            />
            <MetricCard
              title="Calls Today"
              value={stats?.callsToday?.toString() || "0"}
              change={-5}
              changeLabel="vs yesterday"
              icon={Phone}
            />
            <MetricCard
              title="Win Rate"
              value={`${stats?.winRate || 0}%`}
              change={4}
              changeLabel="vs last quarter"
              icon={Target}
              variant="success"
            />
          </>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6 stagger-children">
          <HotLeadsTable />
          <PipelineOverview />
        </div>
        <div className="space-y-6">
          {/* Contract Pipeline Widget */}
          <div className="animate-slide-in-right">
            <ContractPipelineWidget />
          </div>

          {/* AI Sales Coach */}
          <div className="animate-slide-in-right delay-100">
            <AISalesCoach userName={firstName} />
          </div>

          <div className="animate-slide-in-right delay-200">
            <ActivityFeed />
          </div>
          
          {/* Quick Actions */}
          <div className="glass rounded-xl p-5 animate-slide-in-right delay-400">
            <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="secondary" 
                className="justify-start gap-2"
                onClick={() => setAddLeadOpen(true)}
              >
                <Users className="h-4 w-4" />
                Add Lead
              </Button>
              <Button 
                variant="secondary" 
                className="justify-start gap-2"
                onClick={() => setAddDealOpen(true)}
              >
                <Target className="h-4 w-4" />
                New Deal
              </Button>
              <Button 
                variant="secondary" 
                className="justify-start gap-2"
                onClick={() => setLogCallOpen(true)}
              >
                <Phone className="h-4 w-4" />
                Log Call
              </Button>
              <Button 
                variant="secondary" 
                className="justify-start gap-2"
                onClick={handleForecast}
              >
                <TrendingUp className="h-4 w-4" />
                Forecast
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
